<template>
  <div style="margin-bottom: 2em; display: flex; align-items: center">
    <span style="fontsize: 14px" class="has-text-grey">
      {{ setting.label || name }}
    </span>
    <Tooltip :label="setting.label || name" :content="setting.detail" />
    <br />

    <div style="margin-left: 10px">
      <el-slider
        v-if="setting.slider === true"
        show-input
        v-model="value[name]"
        :disabled="setting.disabled ? true : false"
        :required="setting.required"
        :min="setting.min"
        :max="setting.max"
        :step="setting.step ? setting.step : 1"
        style="width: 720px; max-width: 100%"
      />

      <el-input-number
        v-else
        v-model="value[name]"
        :disabled="setting.disabled ? true : false"
        :required="setting.required"
        :min="min"
        :max="max"
        :step="setting.step ? setting.step : 1"
      />
    </div>
  </div>
</template>

<script>
/**
 * Number SmartField - Renders a numeric input with optional slider for number configuration values.
 *
 * @component
 * @description Displays either a slider with input (if setting.slider is true) or a number input spinner.
 * Supports conditional min/max values that depend on other field values, automatically adjusting
 * the current value if it falls outside the new range.
 *
 * @prop {Object} value - Parent object containing the setting value
 * @prop {Object} setting - Field configuration object
 * @prop {string} [setting.label] - Display label for the input (defaults to name)
 * @prop {string} [setting.detail] - Tooltip text explaining the setting
 * @prop {boolean} [setting.slider] - If true, shows slider instead of number input
 * @prop {boolean} [setting.disabled] - If true, disables the input
 * @prop {boolean} [setting.required] - If true, marks the input as required
 * @prop {number} [setting.min] - Minimum value allowed
 * @prop {number} [setting.max] - Maximum value allowed
 * @prop {number} [setting.step=1] - Step increment for the input
 * @prop {Object} [setting.conditional] - Conditional min/max based on other field values
 * @prop {number} [setting.default] - Default value if not set
 * @prop {string} name - Property key in the value object
 *
 * @example
 * <Number
 *   :value="config"
 *   :setting="{ label: 'Timeout', detail: 'Timeout in seconds', min: 1, max: 60, step: 5 }"
 *   name="timeout"
 * />
 *
 * @example
 * // With slider
 * <Number
 *   :value="config"
 *   :setting="{ label: 'Volume', slider: true, min: 0, max: 100, default: 50 }"
 *   name="volume"
 * />
 */
import Tooltip from "../Tooltip.vue";
export default {
  components: { Tooltip },
  props: ["value", "setting", "name"],
  computed: {
    /**
     * Computed minimum value that can be conditional based on other field values.
     *
     * @returns {number} The minimum value for this field
     */
    min() {
      const conditional = this.setting.conditional;
      if (!conditional || !conditional.min) {
        return this.setting.min;
      }

      const dependentKey = Object.keys(conditional.min)[0];
      if (!dependentKey) {
        return this.setting.min;
      }

      const dependentValue = this.value[dependentKey];
      if (!dependentValue) {
        return this.setting.min;
      }

      const minValue = conditional?.min?.[dependentKey]?.[dependentValue];
      if (minValue && this.value[this.name] < minValue) {
        this.$set(this.value, this.name, minValue);
      }
      return minValue;
    },
    /**
     * Computed maximum value that can be conditional based on other field values.
     *
     * @returns {number} The maximum value for this field
     */
    max() {
      const conditional = this.setting.conditional;
      if (!conditional || !conditional.max) {
        return this.setting.max;
      }

      const dependentKey = Object.keys(conditional.max)[0];
      if (!dependentKey) {
        return this.setting.max;
      }

      const dependentValue = this.value[dependentKey];
      if (!dependentValue) {
        return this.setting.max;
      }

      const maxValue = conditional?.max?.[dependentKey]?.[dependentValue];
      if (maxValue && this.value[this.name] > maxValue) {
        this.$set(this.value, this.name, maxValue);
      }
      return maxValue;
    },
  },
  mounted() {
    if (this.value[this.name] == null && typeof this.value === "object") {
      if (this.setting.default != null) {
        this.$set(this.value, this.name, this.setting.default);
      } else {
        this.$set(this.value, this.name, null);
      }
    }
  },
};
</script>
